import React, { useEffect, useRef } from "react";
import {
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useImmer } from "use-immer";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Checkbox } from "primereact/checkbox";
import ReactDOMServer from "react-dom/server";
import markerBlue from "../../svg/markerBlue.webp";
import markerGreen from "../../svg/markerGreen.webp";
import simpleMarkerSvg from "../../svg/simpleMarkerSvg.webp";
import L from "leaflet";

const Map = ({ data, selectedItems, onSelect }) => {
  const [showWichLoc, setShowWichLoc] = useImmer("all");

  const mapRef = useRef(null);
  const drag = useRef({
    key: "None",
    drag: false,
    startLoc: undefined,
  });

  const getBoundedItems = (items, boundingBox) => {
    const { start, end } = boundingBox;
    const minLat = Math.min(start.lat, end.lat);
    const maxLat = Math.max(start.lat, end.lat);
    const minLng = Math.min(start.lng, end.lng);
    const maxLng = Math.max(start.lng, end.lng);

    return items.filter((item) => {
      let pos = [item.lat, item.lng]; // Use lat and lng from the data
      return (
        pos[0] >= minLat &&
        pos[0] <= maxLat &&
        pos[1] >= minLng &&
        pos[1] <= maxLng
      );
    });
  };

  const rectangleRef = useRef(null);

  const dragLength = (start, end) =>
    Math.sqrt(
      Math.pow(start.lat - end.lat, 2) + Math.pow(start.lng - end.lng, 2)
    );

  const MapEvents = () => {
    useMapEvents({
      mousedown(event) {
        drag.current.drag = true;
        drag.current.key = event.originalEvent.altKey
          ? "alt"
          : event.originalEvent.ctrlKey
          ? "ctrl"
          : event.originalEvent.shiftKey
          ? "shift"
          : "none";
        drag.current.startLoc = event.latlng;
        if (rectangleRef.current) {
          mapRef.current.target.removeLayer(rectangleRef.current);
          rectangleRef.current = null;
        }
      },
      mousemove(event) {
        if (drag.current.drag) {
          if (drag.current.key === "none") {
            const lat =
              mapRef.current.target.getCenter().lat -
              (event.latlng.lat - drag.current.startLoc.lat);
            const lng =
              mapRef.current.target.getCenter().lng -
              (event.latlng.lng - drag.current.startLoc.lng);
            mapRef.current.target.panTo(new L.LatLng(lat, lng));
          }
          if (
            drag.current.key === "ctrl" ||
            drag.current.key === "shift" ||
            drag.current.key === "alt"
          ) {
            if (rectangleRef.current) {
              mapRef.current.target.removeLayer(rectangleRef.current);
            }
            const newRect = L.rectangle([drag.current.startLoc, event.latlng], {
              color: "#3388ff",
              weight: 2,
              fillOpacity: 0.1,
            }).addTo(mapRef.current.target);
            rectangleRef.current = newRect;
          }
        }
      },
      mouseup(event) {
        if (
          drag.current.drag &&
          dragLength(drag.current.startLoc, event.latlng) > 0.005
        ) {
          let newSelected = [];
          if (
            drag.current.key === "ctrl" ||
            drag.current.key === "shift" ||
            drag.current.key === "alt"
          ) {
            newSelected = getBoundedItems(data, {
              start: drag.current.startLoc,
              end: event.latlng,
            }).map((x) => x.id); // Updated to use the 'id' field as identifier
          }

          if (onSelect) {
            onSelect(
              newSelected,
              drag.current.key === "shift"
                ? "add"
                : drag.current.key === "alt"
                ? "remove"
                : "select"
            );
          }
        }
        drag.current.drag = false;
        drag.current.key = "none";
        if (rectangleRef.current) {
          mapRef.current.target.removeLayer(rectangleRef.current);
        }
      },
    });
    return null;
  };

  const filterquby = (item) => {
    if (showWichLoc === "all") return true;
    if (showWichLoc === "selected") {
      return selectedItems.includes(item.id); // Updated to use the 'id' field
    }
    if (showWichLoc === "unselected") {
      return !selectedItems.includes(item.id); // Updated to use the 'id' field
    }
  };

  return (
    <div>
      <div className="xl:flex grid justify-between">
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="all">همه</label>
            <div className="flex align-items-center">
              <Checkbox
                inputId="all"
                name="all"
                value="all"
                onChange={() => setShowWichLoc("all")}
                checked={showWichLoc === "all"}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="unselected" className="ml-2">
              انتخاب نشده
            </label>
            <div className="flex align-items-center">
              <Checkbox
                inputId="unselected"
                name="unselected"
                value="unselected"
                onChange={() => setShowWichLoc("unselected")}
                checked={showWichLoc === "unselected"}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="selected" className="ml-2">
              انتخاب شده
            </label>
            <div className="flex align-items-center">
              <Checkbox
                inputId="selected"
                name="selected"
                value="selected"
                onChange={() => setShowWichLoc("selected")}
                checked={showWichLoc === "selected"}
              />
            </div>
          </div>
        </div>
        <div className="hidden xl:block xl:flex gap-3">
          <div className="flex items-center gap-1">
            <div className="border-2 rounded-lg text-xs p-1">
              <span className="text-xs">Ctrl + Left click</span>
            </div>
            <div className="font-bold">: Select</div>
          </div>
          <div className="flex items-center gap-1">
            <div className="border-2 rounded-lg text-xs p-1">
              <span className="text-xs">Shift + Left click</span>
            </div>
            <div className="font-bold">: add</div>
          </div>
          <div className="flex items-center gap-1">
            <div className="border-2 rounded-lg text-xs p-1">
              <span className="text-xs">Alt + Left click</span>
            </div>
            <div className="font-bold">: remove</div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <MapContainer
          center={[35.699739, 51.338097]}
          zoom={10}
          dragging={false}
          boxZoom={false}
          zoomControl={false}
          whenReady={(mapInstance) => {
            mapRef.current = mapInstance;
          }}
          style={{
            height: "85vh",
            width: "100%",
            borderRadius: "12px",
            zIndex: "1",
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MarkerClusterGroup>
            {data
              ?.filter((x) => filterquby(x))
              .map((quby) => {
                const lat = +quby.lat; // Use 'lat' from the data
                const lng = +quby.lng; // Use 'lng' from the data

                if (isNaN(lat) || isNaN(lng)) {
                  return null;
                }

                return (
                  <Marker
                    key={quby.id} // Use 'id' from the data
                    position={[lat, lng]}
                    icon={
                      new L.divIcon({
                        html: ReactDOMServer.renderToString(
                          <div className="grid gap-2 justify-center items-start -mt-7 mr-2">
                            <img
                              src={
                                quby.pendingDistribution
                                  ? simpleMarkerSvg
                                  : selectedItems.includes(quby.id)
                                  ? markerBlue
                                  : markerGreen
                              }
                              style={{
                                width: "30px",
                                height: "30px",
                              }}
                            />
                            <div className="border border-black rounded-2xl -mt-2"></div>
                          </div>
                        ),
                        iconSize: new L.Point(0, 0),
                      })
                    }
                  >
                    <Tooltip sticky>
                      <div>{quby.userName}</div>{" "}
                      {/* Assuming 'userName' to show */}
                    </Tooltip>
                  </Marker>
                );
              })}
          </MarkerClusterGroup>
          <MapEvents />
        </MapContainer>
      </div>
    </div>
  );
};

export default Map;
